import { useCallback, useRef, useState } from 'react'
import { useToasts } from 'react-toast-notifications'
import { Failure, describeFailure } from '../../api/failureReason'
import { AdvancedApiError, advancedPost, advancedPut } from '../../api/client'
import { useAdvancedOverviewState } from '../../api/AdvancedOverviewProvider'
import { AdvancedTrackEndReason, AdvancedTrackLead } from '../../api/types'

// An omitted lead defaults to the track's owner-role member (ADR-093); `acceptNow` is for ADR-098's tracks.
export type TrackStartRequest = { trackKey: string; lead?: AdvancedTrackLead; acceptNow?: boolean }

export type TrackEndRequest = { endReason: AdvancedTrackEndReason; next: string | null; nextLead?: AdvancedTrackLead }

export type TrackTransitionsState = {
  start: (request: TrackStartRequest, onStarted?: () => void) => void
  end: (enrollmentId: string, request: TrackEndRequest, onEnded?: () => void) => void
  changeLead: (enrollmentId: string, lead: AdvancedTrackLead) => void
  // `startsOn` is a future Monday, `YYYY-MM-DD` (ADR-097); null for a track that starts on acceptance.
  accept: (enrollmentId: string, startsOn: string | null, onAccepted?: () => void) => void
  busy: boolean
  // Shown on the bar the coach acted on, not as a toast: that is where they are looking.
  failure: Failure | null
  dismissFailure: () => void
}

// Every surface that reads the overview updates together when this refreshes it (ADR-092).
export const useTrackTransitions = (uid: string): TrackTransitionsState => {
  const overview = useAdvancedOverviewState()
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<{ uid: string; failure: Failure } | null>(null)
  const { addToast } = useToasts()
  const currentUid = useRef(uid)
  currentUid.current = uid
  const refresh = useCallback(() => (overview.status === 'ready' ? overview.retry() : undefined), [overview])

  const run = useCallback(
    (write: () => Promise<unknown>, onDone?: () => void) => {
      const requestedFor = currentUid.current
      setBusy(true)
      setFailure(null)
      write()
        .then(() => {
          // `onDone` edits the plan on screen, which is another patient's if the provider switched meanwhile.
          if (currentUid.current === requestedFor) {
            onDone?.()
          }
          refresh()
        })
        .catch((caught: unknown) => {
          // A 404 here is usually a track someone else already ended.
          const stale = caught instanceof AdvancedApiError && (caught.status === 409 || caught.status === 404)
          // A toast, not the bar: the refresh can take away the bar the coach acted on.
          if (stale) {
            addToast("This patient's tracks had changed since the page loaded — showing the latest now.", {
              appearance: 'info',
            })
            refresh()
            return
          }
          setFailure({ uid: requestedFor, failure: describeFailure("Failed to update this patient's tracks.", caught) })
        })
        .finally(() => setBusy(false))
    },
    [refresh, addToast]
  )

  const path = `/patients/${encodeURIComponent(uid)}/tracks`

  const start = useCallback(
    ({ trackKey, lead, acceptNow }: TrackStartRequest, onStarted?: () => void) =>
      run(() => advancedPost(path, { trackKey, lead, ...(acceptNow ? { acceptNow } : null) }), onStarted),
    [run, path]
  )

  const end = useCallback(
    (enrollmentId: string, { endReason, next, nextLead }: TrackEndRequest, onEnded?: () => void) =>
      run(
        () => advancedPost(`${path}/${encodeURIComponent(enrollmentId)}/end`, { endReason, next, nextLead }),
        onEnded
      ),
    [run, path]
  )

  const changeLead = useCallback(
    (enrollmentId: string, lead: AdvancedTrackLead) =>
      run(() => advancedPut(`${path}/${encodeURIComponent(enrollmentId)}/lead`, { lead })),
    [run, path]
  )

  const accept = useCallback(
    (enrollmentId: string, startsOn: string | null, onAccepted?: () => void) =>
      run(
        () => advancedPost(`${path}/${encodeURIComponent(enrollmentId)}/accept`, startsOn ? { startsOn } : {}),
        onAccepted
      ),
    [run, path]
  )

  const dismissFailure = useCallback(() => setFailure(null), [])

  return {
    start,
    end,
    changeLead,
    accept,
    busy,
    failure: failure?.uid === uid ? failure.failure : null,
    dismissFailure,
  }
}
